// src/components/WorkflowCreator/panels/NodePalette.jsx
// Panel lateral con los nodos disponibles para arrastrar

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Bot, ShoppingBag, MessageCircle, Globe, Database,
  GitBranch, Timer, Clock, Mail, Hash, MessageSquare,
  Send, Shuffle, FileText, AlertTriangle, Hourglass,
  Sparkles, Webhook, Settings, Plug, Bell, ChevronDown,
  ChevronRight, Search, GripVertical, Cloud
} from 'lucide-react';
import { NODE_CATEGORIES, NODE_DEFINITIONS, getNodesByCategory } from '../nodes/nodeDefinitions';
import WorkflowService from '../../../services/workflow.service';

// Map icon names to components
const ICON_MAP = {
  Zap, Bot, ShoppingBag, MessageCircle, Globe, Database,
  GitBranch, Timer, Clock, Mail, Hash, MessageSquare,
  Send, Shuffle, FileText, AlertTriangle, Hourglass,
  Sparkles, Webhook, Settings, Plug, Bell, Cloud
};

const getIcon = (iconName) => ICON_MAP[iconName] || Zap;

/**
 * Draggable node item
 */
const DraggableNode = ({ nodeType, definition }) => {
  const Icon = getIcon(definition.icon);

  const onDragStart = (event) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.02, x: 4 }}
      whileTap={{ scale: 0.98 }}
      draggable
      onDragStart={onDragStart}
      className={`
        flex items-center gap-3 p-3 rounded-lg cursor-grab
        bg-[#1a1a2e]/60 hover:bg-[#1a1a2e]
        border border-transparent hover:border-white/10
        transition-all duration-200 group
      `}
    >
      {/* Drag handle */}
      <GripVertical className="w-4 h-4 text-gray-600 group-hover:text-gray-400 flex-shrink-0" />
      
      {/* Icon */}
      <div className={`
        w-9 h-9 rounded-lg flex-shrink-0
        bg-gradient-to-br ${definition.color}
        flex items-center justify-center
        shadow-lg group-hover:shadow-xl
        transition-shadow duration-200
      `}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-white text-sm font-medium truncate">
          {definition.label}
        </h4>
        <p className="text-gray-500 text-xs truncate">
          {definition.description}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Collapsible category section
 */
const CategorySection = ({ category, isOpen, onToggle }) => {
  const categoryInfo = NODE_CATEGORIES[category.toUpperCase()];
  const nodes = getNodesByCategory(category);
  const CategoryIcon = getIcon(categoryInfo?.icon || 'Zap');

  if (!categoryInfo || nodes.length === 0) return null;

  return (
    <div className="mb-2">
      {/* Category header */}
      <button
        onClick={onToggle}
        className={`
          w-full flex items-center gap-2 p-2 rounded-lg
          hover:bg-white/5 transition-colors
          text-left
        `}
      >
        <div className={`
          w-6 h-6 rounded flex items-center justify-center
          bg-gradient-to-br ${categoryInfo.color} opacity-80
        `}>
          <CategoryIcon className="w-3 h-3 text-white" />
        </div>
        <span className="text-white font-medium text-sm flex-1">
          {categoryInfo.label}
        </span>
        <span className="text-gray-500 text-xs mr-2">
          {nodes.length}
        </span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Nodes list */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-2 pt-1 space-y-1">
              {nodes.map((node) => (
                <DraggableNode
                  key={node.type}
                  nodeType={node.type}
                  definition={node}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Catalog-only trigger item — backend registered, not yet locally configured
 */
const CatalogTriggerItem = ({ entry }) => (
  <div
    className="flex items-center gap-3 p-3 rounded-lg opacity-60 cursor-not-allowed select-none
      bg-[#1a1a2e]/40 border border-dashed border-white/10"
    title="This trigger type is registered in the backend but not yet fully configured in the UI"
  >
    <Cloud className="w-4 h-4 text-gray-600 flex-shrink-0" />
    <div className="w-9 h-9 rounded-lg flex-shrink-0 bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
      <Zap className="w-4 h-4 text-gray-400" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <h4 className="text-gray-400 text-sm font-medium truncate">
          {entry.display_name || entry.trigger_type}
        </h4>
        <span className="text-[9px] font-semibold text-gray-500 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0">
          backend
        </span>
      </div>
      <p className="text-gray-600 text-xs truncate">
        {entry.description || entry.trigger_type}
      </p>
    </div>
  </div>
);

/**
 * Node Palette - Panel lateral con nodos arrastrables
 */
const NodePalette = ({ isOpen = true }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogOnlyTriggers, setCatalogOnlyTriggers] = useState([]);
  const [openCategories, setOpenCategories] = useState({
    triggers: true,
    ai: true,
    integrations: false,
    notifications: false,
    data: false,
    control: false,
    system: false,
  });

  // Fetch backend trigger catalog on mount to discover backend-registered types
  useEffect(() => {
    WorkflowService.getTriggerCatalog()
      .then((result) => {
        if (!result.success) return;
        const known = new Set(Object.keys(NODE_DEFINITIONS));
        const backendOnly = (result.data.entries || []).filter(
          (e) => e.trigger_type && !known.has(e.trigger_type)
        );
        setCatalogOnlyTriggers(backendOnly);
      })
      .catch(() => {});
  }, []);

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter nodes based on search
  const filteredNodes = searchQuery
    ? Object.entries(NODE_DEFINITIONS).filter(([type, def]) =>
        def.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        def.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-[#0d0d0d] border-r border-white/10">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <h2 className="text-white font-semibold text-lg mb-3">Nodes</h2>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg py-2 pl-10 pr-4 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/25"
          />
        </div>
      </div>

      {/* Nodes list */}
      <div className="flex-1 overflow-y-auto p-3 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {searchQuery ? (
          // Search results
          <div className="space-y-1">
            {filteredNodes && filteredNodes.length > 0 ? (
              filteredNodes.map(([type, def]) => (
                <DraggableNode key={type} nodeType={type} definition={def} />
              ))
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">
                No nodes found
              </p>
            )}
          </div>
        ) : (
          // Category list
          <>
            {/* Triggers: local definitions + catalog-only backend triggers */}
            <div className="mb-2">
              <CategorySection
                category="triggers"
                isOpen={openCategories.triggers}
                onToggle={() => toggleCategory('triggers')}
              />
              {openCategories.triggers && catalogOnlyTriggers.length > 0 && (
                <div className="pl-2 pt-1 space-y-1">
                  {catalogOnlyTriggers.map((entry) => (
                    <CatalogTriggerItem key={entry.trigger_type} entry={entry} />
                  ))}
                </div>
              )}
            </div>
            <CategorySection
              category="ai"
              isOpen={openCategories.ai}
              onToggle={() => toggleCategory('ai')}
            />
            <CategorySection
              category="integrations"
              isOpen={openCategories.integrations}
              onToggle={() => toggleCategory('integrations')}
            />
            <CategorySection
              category="notifications"
              isOpen={openCategories.notifications}
              onToggle={() => toggleCategory('notifications')}
            />
            <CategorySection
              category="data"
              isOpen={openCategories.data}
              onToggle={() => toggleCategory('data')}
            />
            <CategorySection
              category="control"
              isOpen={openCategories.control}
              onToggle={() => toggleCategory('control')}
            />
            <CategorySection
              category="system"
              isOpen={openCategories.system}
              onToggle={() => toggleCategory('system')}
            />
          </>
        )}
      </div>

      {/* Help text */}
      <div className="p-3 border-t border-white/10">
        <p className="text-gray-500 text-xs text-center">
          Drag nodes to the canvas to build your workflow
        </p>
      </div>
    </div>
  );
};

export default NodePalette;
